import { globalT } from "../../lang";
import rpnClient from "../../network";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import { Search } from "@mui/icons-material";
import { LineChart } from '@mui/x-charts/LineChart';
import IntlMessage from "../../components/IntlMessage";
import { SubmitHandler, useForm } from "react-hook-form";
import useSettingsStore from "../../store/slices/settings";
import InputComponent from "../../components/forms/InputComponent";

import Label from "../../components/forms/Label";
import CommunityLight from "../../commons/models/CommunityLight";
import MemberChartItem from "../../commons/models/MemberChartItem";
import NotificationManager from "../../components/notifications/NotificationManager";
import SelectAsyncPaginate from "../../components/forms/select/SelectAsyncPaginate";
import EmptyResult from "../../components/requests/EmptyResult";
import { CardBody, CardLabel, CardTitle } from "../../components/bootstrap/Card";
import { Card, CardHeader, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import BsButton from "../../components/bootstrap/BsButton";

type FormType = {
    community?: { id: string, name: string }
    period?: {label: string, value: string}
    year?: {label: string, value: string}
    month?: {label: string, value: string}
}

const LoginStatistic = () => {

    const setRequestGlobalLoader = useSettingsStore(s => s.setRequestGlobalLoader)
    const [chartDatas, setChartDatas] = useState<MemberChartItem[]>([]);
    const [showDataTable, setShowDataTable] = useState<boolean>(false);

    useEffect(() => {
        getChartDatas({});
    }, [])

    const {
        watch,
        control,
        setValue,
        handleSubmit,
        formState: {errors},
    } = useForm<FormType>({
        mode: 'onChange',
        defaultValues: {}
    });

    const period = watch('period')?.value;

    const onSubmit: SubmitHandler<FormType> = async (data) => {
        if(data?.period?.value === 'month' && (!data.year || !data.month)) {
            NotificationManager.warning(globalT("errors.badForm"));
            return;
        }

        let filter: any = {};

        if(data.period && data.period.value !== '13month') {
            filter.period = data.period.value;
        }

        if(data.year) {
            filter.year = Number(data.year.value);
        } else {
            filter.year = new Date().getFullYear();
        }

        if(data.month) {
            filter.month = filter.year+'-'+data.month.value;
        }

        if(data.community) {
            filter.community_reference = data.community.id;
        }

        getChartDatas(filter);
    }

    const getChartDatas = (filter: any) => {
        setRequestGlobalLoader(true)
        rpnClient.auth.users.getChartDatas(filter).then((response) => {
            setChartDatas(response.data);
        }).finally(() => {
            setRequestGlobalLoader(false)
        })
    }

    return (
        <div className="w-100 mb-5">
            <h2 className="px-5 mb-5"><IntlMessage id="statistics.login" /></h2>
            <form onSubmit={handleSubmit(onSubmit)} className="container">
                <div className="row px-5 align-items-end">
                    <div className="col-sm-12 col-md-4">
                        <Label>
                            <IntlMessage id='communities'/>
                        </Label>
                        <InputComponent
                            id="communities"
                            errors={errors}
                            control={control}
                            name="community"
                            isRequired={false}
                            componentType="custom"
                            // @ts-ignore
                            render={({ field }) => (
                                <div>
                                    <SelectAsyncPaginate<{ id: string, name: string }>
                                        {...field}
                                        defaultOptions
                                        getOptionValue={(option) => option.id}
                                        getOptionLabel={(option) => option.name}
                                        mapDataToOptions={(data: CommunityLight[]) => {
                                            return data
                                                .map((i) => ({ id: i.id, name: i.name }))
                                        }}
                                        fetchData={async (page, pageSize, search) => await rpnClient.community.self.getAllActiveSimple({
                                            page,
                                            pageSize,
                                            search
                                        })}
                                    />
                                </div>
                            )}
                        />
                    </div>
                    <div className="col-sm-12 col-md-2">
                        <InputComponent
                            name="period"
                            errors={errors}
                            control={control}
                            isRequired={false}
                            setValue={setValue}
                            noPlaceholder={false}
                            componentType="select"
                            options={rpnClient.staticTypes.periods}
                            placeholder={<IntlMessage id="date.period" />}
                            label={<IntlMessage id="date.period" />}
                        />
                    </div>
                    { period && period !== '13month' && (
                        <div className={'col-sm-12 col-md-2'}>
                            <InputComponent
                                name="year"
                                errors={errors}
                                control={control}
                                isRequired={false}
                                setValue={setValue}
                                noPlaceholder={false}
                                componentType="select"
                                options={rpnClient.staticTypes.years}
                                placeholder={<IntlMessage id="payment.form.year" />}
                                label={<IntlMessage id="payment.form.year" />}
                            />
                        </div>
                    )}
                    { period && period === 'month' && (
                        <div className="col-sm-12 col-md-2">
                            <InputComponent
                                name="month"
                                errors={errors}
                                control={control}
                                isRequired={false}
                                setValue={setValue}
                                noPlaceholder={false}
                                componentType="select"
                                options={rpnClient.staticTypes.months}
                                placeholder={<IntlMessage id="payment.form.month" />}
                                label={<IntlMessage id="payment.form.month" />}
                            />
                        </div>
                    )}
                    <div className='col-sm-12 col-md-2 py-1'>
                        <Button
                            size='medium'
                            type='submit'
                            color='primary'
                            variant='contained'
                            endIcon={<Search />}
                            className='text-inherit px-3'>
                            <IntlMessage id='button.refresh' />
                        </Button>
                    </div>
                </div>
                <LineChart
                    height={400}
                    grid={{ vertical: true, horizontal: true }}
                    series={[{ data: chartDatas.map(d => d.value) }]}
                    xAxis={[{ scaleType: 'point', data: chartDatas.map(d => d.time) }]}
                />
                <div className="row justify-content-end px-5">
                    <div className="col-sm-12 col-md-4 row justify-content-end">
                        <BsButton
                            className='py-2'
                            color='primary'
                            isLight
                            icon='bx-table'
                            onClick={() => {
                                setShowDataTable(!showDataTable)
                            }}
                        >
                            <IntlMessage id="statistics.datas.show" />
                        </BsButton>
                    </div>
                </div>
            </form>
            { showDataTable && (
                <div className="col-sm-12 col-md-12 col-lg-12">
                    <Card>
                        <CardHeader className='pb-0'>
                            <CardLabel icon='bx-user-pin'>
                                <CardTitle><IntlMessage id="statistics.datas.table" /></CardTitle>
                            </CardLabel>
                        </CardHeader>

                        <CardBody className="">
                            <TableContainer component={Paper}>
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                    <TableHead style={{ background: '#09ce87', color: 'white' }}>
                                        <TableRow>
                                            <TableCell><IntlMessage id='date.period' /></TableCell>
                                            <TableCell><IntlMessage id='statistics.datas' /></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        { chartDatas.length <= 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={2}>
                                                    <EmptyResult emptyText={"pas de données"} />
                                                </TableCell>
                                            </TableRow>
                                        ) :
                                        chartDatas.map((item: MemberChartItem, index: number) => (
                                            <TableRow
                                                key={index}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell component="th" scope="row">{item.time}</TableCell>
                                                <TableCell>{item.value}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardBody>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default LoginStatistic;
